import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import * as DataTableConfigActions from 'notebook/data-tables/DataTableConfigActions';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import {DATA_TABLE_COLUMN_SHAPE} from 'notebook/structured-tables/StructuredTableConstants';
import DataTableColumnPicker from 'notebook/tables/lookup-field/DataTableColumnPicker';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface EnhancedProps {
  dataTableConfigs: Array<DataTableConstants.FromServerDataTableConfig> | null;
}

class DataTableConfigFieldSelectorConnector extends DataConnector<
  DataTableConfigFieldSelectorProps,
  EnhancedProps
> {
  shouldSubscribeToRedux = false;
  stores = [DataTableConfigStore];

  enhanceProps(): EnhancedProps {
    let dataTableConfigs = this.dataTableConfigStore().getDataTableConfigs();
    if (dataTableConfigs != null) {
      dataTableConfigs = dataTableConfigs.filter(
        (dataTableConfig) =>
          dataTableConfig.columns.length > 0 &&
          dataTableConfig.assaySchema.schemaType === ASSAY_TYPES.ASSAY_RESULT &&
          !isArchived(dataTableConfig.assaySchema)
      );
    }
    return {dataTableConfigs};
  }
}

interface DataTableConfigFieldSelectorProps {
  dataTableConfig: DataTableConstants.FromServerDataTableConfig | null;
  dataTableColumn: DataTableConstants.FromServerDataTableColumn | null;
  onDataTableColumnChange: (
    dataTableConfig: DataTableConstants.FromServerDataTableConfig | null,
    dataTableColumn: DataTableConstants.FromServerDataTableColumn | null
  ) => void;
}

class DataTableConfigFieldSelector extends React.Component<
  DataTableConfigFieldSelectorProps & EnhancedProps
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    dataTableConfig: DataTableConstants.DATA_TABLE_CONFIG_SHAPE,
    dataTableColumn: DATA_TABLE_COLUMN_SHAPE,
    onDataTableColumnChange: PropTypes.func.isRequired,

    // Props provided by connect to DataTableConfigStore
    dataTableConfigs: PropTypes.arrayOf(DataTableConstants.DATA_TABLE_CONFIG_SHAPE.isRequired),
  };

  componentDidMount(): void {
    this._trySelectDefaultConfigAndColumn(this.props);
    DataTableConfigActions.fetchAllDataTableConfigs();
  }

  UNSAFE_componentWillReceiveProps(nextProps: DataTableConfigFieldSelectorProps & EnhancedProps): void {
    this._trySelectDefaultConfigAndColumn(nextProps);
  }

  _trySelectDefaultConfigAndColumn(props: DataTableConfigFieldSelectorProps & EnhancedProps): void {
    const {dataTableConfig, dataTableColumn, dataTableConfigs} = props;

    if (dataTableConfig == null && dataTableColumn == null && dataTableConfigs != null) {
      if (dataTableConfigs.length > 0 && dataTableConfigs[0].columns.length > 0) {
        props.onDataTableColumnChange(dataTableConfigs[0], dataTableConfigs[0].columns[0]);
      }
    }
  }

  _handleDataTableConfigChange = (configId: number): void => {
    const dataTableConfig =
      configId == null ? null : _.findWhere(this.props.dataTableConfigs!, {id: configId})!;

    this.props.onDataTableColumnChange(
      dataTableConfig,
      dataTableConfig == null ? null : dataTableConfig.columns[0]
    );
  };

  render(): JSX.Element {
    return (
      <div>
        {this.props.dataTableConfigs == null ? (
          <div className="text-center">
            <HorizontalThrobber />
          </div>
        ) : (
          <div>
            <FormGroup>
              <Label className="u-text-uppercase">{MODEL_NAMES.Result_Table} Schema</Label>
              <SelectDropdown
                displayName={this.props.dataTableConfig ? this.props.dataTableConfig.name : 'Select a schema'}
                menu={
                  <SearchableMenu
                    onSelect={(selectedValue) => {
                      this._handleDataTableConfigChange(parseInt(selectedValue, 10));
                    }}
                    optionDatas={this.props.dataTableConfigs.map((dataTableConfig) => ({
                      value: dataTableConfig.id,
                      name: dataTableConfig.name,
                    }))}
                    selectedValue={this.props.dataTableConfig?.id ?? null}
                    width={MenuWidth.FULL}
                  />
                }
              />
            </FormGroup>
            <DataTableColumnPicker
              allColumns={this.props.dataTableConfig ? this.props.dataTableConfig.columns : []}
              selectedColumnId={this.props.dataTableColumn && this.props.dataTableColumn.id}
              onSelectedColumnChange={(column: DataTableConstants.FromServerDataTableColumn) => {
                this.props.onDataTableColumnChange(this.props.dataTableConfig, column);
              }}
            />
          </div>
        )}
      </div>
    );
  }
}

export default DataTableConfigFieldSelectorConnector.connect(DataTableConfigFieldSelector);
